import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Accordion } from '@risksmart-app/atomic-ui';
import type { ReactElement } from 'react';
import { isValidElement, useMemo, useState } from 'react';

import ConditionalFields from './ConditionalField';
import DraggableField from './DraggableField';
import { DraggableSection } from './DraggableSection';
import type { SectionConfig } from './hooks/useSectionManager';
import style from './style.module.scss';

type Element = {
  key: string;
  value: ReactElement<{ condition?: unknown } & Record<string, unknown>>;
};

interface Props {
  sections: SectionConfig[];
  draggedFieldId: string | null;
  handleFieldReorderInSection: (
    sectionId: string,
    newFieldOrder: string[]
  ) => void;
  setDraggedFieldId: (fieldId: string | null) => void;
  fieldComponents: Element[];
  isEditMode: boolean;
  isReadOnly?: boolean;
  /** When true, render fields flat without accordion section wrappers. */
  flatLayout?: boolean;
}

export function SectionedFormLayout({
  sections,
  draggedFieldId: _draggedFieldId,
  handleFieldReorderInSection,
  setDraggedFieldId,
  fieldComponents,
  isEditMode,
  flatLayout = false,
}: Props) {
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [overFieldId, setOverFieldId] = useState<string | null>(null);

  const defaultOpenSections = useMemo(
    () => sections.map((s) => s.SectionId),
    [sections]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    })
  );

  const fieldComponentMap = fieldComponents.reduce(
    (acc, element) => {
      acc[element.key] = element.value;

      return acc;
    },
    {} as Record<string, Element['value']>
  );

  const isFieldVisible = (fieldId: string): boolean => {
    const component = fieldComponentMap[fieldId];
    if (
      component &&
      isValidElement(component) &&
      component.type === ConditionalFields
    ) {
      return !!component.props?.condition || isEditMode;
    }

    return true;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = active.id as string;
    setActiveFieldId(activeId);
    setDraggedFieldId(activeId);
    setOverFieldId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveFieldId(null);
      setDraggedFieldId(null);

      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeSection = sections.find((s) => s.fields.includes(activeId));
    const overSection = sections.find((s) => s.fields.includes(overId));

    if (
      activeSection &&
      overSection &&
      activeSection.SectionId === overSection.SectionId
    ) {
      const oldIndex = activeSection.fields.indexOf(activeId);
      const newIndex = activeSection.fields.indexOf(overId);

      if (oldIndex !== newIndex) {
        const newFields = arrayMove(activeSection.fields, oldIndex, newIndex);
        handleFieldReorderInSection(activeSection.SectionId, newFields);
      }
    }

    setActiveFieldId(null);
    setDraggedFieldId(null);
  };

  const handleDragOver = (event: {
    active: { id: string | number };
    over: { id: string | number } | null;
  }) => {
    if (event.over && activeFieldId) {
      const overId = event.over.id as string;
      if (sections.some((s) => s.fields.includes(overId))) {
        setOverFieldId(overId);
      } else {
        setOverFieldId(null);
      }
    }
  };

  const renderFieldList = (sectionFields: string[]) =>
    sectionFields
      .filter((fieldId) => {
        const component = fieldComponentMap[fieldId];

        return component !== null && component !== undefined;
      })
      .filter(isFieldVisible)
      .map((fieldId) => {
        const component = fieldComponentMap[fieldId];

        return (
          <DraggableField
            key={fieldId}
            value={fieldId}
            isDraggable={isEditMode}
            onDragStart={() => setDraggedFieldId(fieldId)}
            onDragEnd={() => setDraggedFieldId(null)}
            hasInsertionLine={
              overFieldId === fieldId && activeFieldId !== fieldId
            }
          >
            {component}
          </DraggableField>
        );
      });

  if (flatLayout && sections.length > 0) {
    // Flat layout ignores section boundaries — render every field across all
    // sections in order. Otherwise hardcoded `<DefaultSection>` markers would
    // silently drop fields whenever the `form_sections` flag is off.
    const allFlatFields = sections.flatMap((s) => s.fields);

    return (
      <DndContext
        sensors={isEditMode ? sensors : []}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <SortableContext
          items={allFlatFields}
          strategy={verticalListSortingStrategy}
        >
          <div className={isEditMode ? style.reorderGroup : undefined}>
            {renderFieldList(allFlatFields)}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeFieldId ? (
            <div
              className={'opacity-50 bg-white p-4 rounded border border-teal'}
            >
              {fieldComponentMap[activeFieldId]}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }

  return (
    <DndContext
      sensors={isEditMode ? sensors : []}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <Accordion
        multiple
        defaultValue={defaultOpenSections}
        className={style.reorderGroup}
      >
        {sections.map((section) => (
          <DraggableSection key={section.SectionId} section={section}>
            <SortableContext
              items={section.fields}
              strategy={verticalListSortingStrategy}
            >
              {renderFieldList(section.fields)}
            </SortableContext>
          </DraggableSection>
        ))}
      </Accordion>
      <DragOverlay>
        {activeFieldId ? (
          <div className={'opacity-50 bg-white p-4 rounded border border-teal'}>
            {fieldComponentMap[activeFieldId]}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
